/**
 * Teams, Gruppen und Freigaben (Entwicklerbericht „Teams, Gruppen, Berechtigungen“).
 *
 * - Ein Team ist der organisatorische Rahmen. Die Aufnahme ins Team öffnet KEINE Boards.
 * - Gruppen gehören zu einem Team und sind Freigabe- und Empfängerziel (keine Untergruppen).
 * - Boardrechte entstehen nur aus Freigaben (board_grant) mit Herkunft: direkt (Person),
 *   Gruppe oder Gastlink. Es zählt die höchste gültige Rolle. Besitzer nur direkt.
 */
import type { Role } from './types.js';

export const TEAM_ROLES = ['member', 'admin', 'owner'] as const;
export type TeamRole = (typeof TEAM_ROLES)[number];

export const TEAM_ROLE_LABELS: Record<TeamRole, string> = {
  member: 'Mitglied',
  admin: 'Administrator',
  owner: 'Besitzer',
};

export function teamRoleAtLeast(role: TeamRole | null | undefined, min: TeamRole): boolean {
  if (!role) return false;
  return TEAM_ROLES.indexOf(role) >= TEAM_ROLES.indexOf(min);
}

export const GRANT_SOURCES = ['user', 'group', 'link'] as const;
export type GrantSource = (typeof GRANT_SOURCES)[number];

export const GRANT_SOURCE_LABELS: Record<GrantSource, string> = {
  user: 'Direkt',
  group: 'Gruppe',
  link: 'Gastlink',
};

/** Gruppen und Links vergeben höchstens „Bearbeiter“ */
export const MAX_SHARED_ROLE: Role = 'editor';

export const GROUP_COLORS = ['gray', 'blue', 'green', 'yellow', 'orange', 'red', 'purple', 'pink'] as const;
export type GroupColor = (typeof GROUP_COLORS)[number];

export interface Team {
  id: string;
  name: string;
  description: string | null;
  /** Rolle der angemeldeten Person */
  myRole: TeamRole;
  memberCount: number;
  createdAt: string;
}

export interface TeamMember {
  id: string;
  name: string;
  email: string;
  image: string | null;
  /** „Funktion · Firma“ aus dem Profil (leer, wenn nicht freigegeben) */
  headline?: string;
  role: TeamRole;
  groupIds: string[];
  createdAt: string;
}

export interface TeamInvitation {
  id: string;
  email: string;
  role: TeamRole;
  invitedBy: string | null;
  /** Name der einladenden Person (L01: nachvollziehbar) */
  invitedByName: string | null;
  createdAt: string;
  expiresAt: string | null;
  /** Gruppen, die erst mit dem Beitritt wirksam werden (L01) */
  groupIds: string[];
}

/** Offene Teameinladung an die eigene Adresse (Annehmen/Ablehnen in der App, F03) */
export interface MyTeamInvitation {
  id: string;
  teamId: string;
  teamName: string;
  role: TeamRole;
  inviterName: string | null;
  expiresAt: string | null;
  /** Gruppen, die mit dem Beitritt wirksam werden */
  groups: string[];
}

export interface TeamBlock {
  userId: string;
  name: string;
  email: string;
  createdAt: string;
}

export interface TeamGroup {
  id: string;
  teamId: string;
  name: string;
  description: string | null;
  color: GroupColor;
  memberIds: string[];
}

/** Eine einzelne Freigabequelle für eine Person auf einem Board */
export interface AccessSource {
  source: GrantSource;
  role: Role;
  /** Gruppe oder Gastlink (Name), bei direkten Freigaben leer */
  via: { id: string; name: string } | null;
}

/** Kategorien des Teamprotokolls (Filter, Paket 4) */
export const PROTOCOL_CATEGORIES = ['mitglieder', 'gruppen', 'freigaben', 'struktur', 'sicherheit', 'team'] as const;
export type ProtocolCategory = (typeof PROTOCOL_CATEGORIES)[number];

export const PROTOCOL_CATEGORY_LABELS: Record<ProtocolCategory, string> = {
  mitglieder: 'Mitglieder und Einladungen',
  gruppen: 'Gruppen',
  freigaben: 'Boards und Freigaben',
  struktur: 'Bereiche und Ordner',
  sicherheit: 'Sperren und Notfälle',
  team: 'Team',
};

/** Welche Aktion zu welcher Kategorie gehört (Server filtert damit, die Oberfläche zeigt das Etikett) */
export const PROTOCOL_ACTIONS: Record<ProtocolCategory, string[]> = {
  team: ['team.create', 'team.update', 'team.branding', 'library.settings', 'library.publish'],
  mitglieder: ['member.add', 'member.join', 'member.invite', 'member.invite.resend', 'member.invite.revoke', 'member.invite.decline', 'member.role', 'member.remove', 'member.leave'],
  gruppen: ['group.create', 'group.update', 'group.delete', 'group.members', 'member.groups'],
  freigaben: ['board.adopt', 'board.transfer', 'board.recover', 'grant.add', 'grant.role', 'grant.remove'],
  struktur: [
    'area.create',
    'area.update',
    'area.delete',
    'area.member.add',
    'area.member.role',
    'area.member.remove',
    'area.member.leave',
    'area.claim',
    'folder.create',
    'folder.update',
    'folder.delete',
  ],
  sicherheit: ['member.emergency', 'member.unblock', 'external.revoke'],
};

export function protocolCategory(action: string): ProtocolCategory {
  for (const c of PROTOCOL_CATEGORIES) if (PROTOCOL_ACTIONS[c].includes(action)) return c;
  return 'team';
}

export interface ProtocolEntry {
  id: string;
  actorId: string | null;
  actorName: string | null;
  action: string;
  category: ProtocolCategory;
  targetType: string;
  targetId: string | null;
  /** Betroffene Person bzw. Gruppe, soweit die betrachtende Person sie sehen darf */
  targetName: string | null;
  /** Satz ohne Handelnden: „hat Anna als Administrator aufgenommen“ */
  summary: string;
  /** Vorher/Nachher in Worten, z. B. „Hinzugefügt: Anna · Entfernt: Ben“ */
  details: string[];
  /** Ob die Person nach einer Sperre/Entfernung betroffen ist (Sicherheitsereignis) */
  important: boolean;
  createdAt: string;
}

export interface ProtocolPage {
  entries: ProtocolEntry[];
  /** Für „Mehr laden“; null = Ende */
  nextCursor: string | null;
}

/** Auswirkungen einer Gruppenlöschung (L01, U03) – nur sichtbare Boards mit Namen */
export interface GroupImpact {
  members: number;
  boards: { id: string; title: string; role: Role; losing: string[] }[];
  /** Die Gruppe ist auch für Boards freigegeben, die die betrachtende Person nicht sieht */
  hiddenBoards: boolean;
  /** Erinnerungsregeln auf sichtbaren Boards, die die Gruppe als Empfänger nennen */
  reminderRules: number;
}

/* ---------- 0.24: Freigabedialog, Zugriff erklären, Verwaltung ---------- */

/** Rollen, die Gruppen und Links vergeben können */
export const SHARED_ROLES = ['viewer', 'commenter', 'editor'] as const;
export type SharedRole = (typeof SHARED_ROLES)[number];

/** Eintrag im Freigabedialog „Personen & Gruppen“ (nur Besitzer) */
export interface SharingPerson {
  id: string;
  name: string;
  email: string;
  image: string | null;
  /** persönlich eingestellte Rolle; null = Zugriff nur über Link */
  direct: Role | null;
  /** tatsächlich geltende Rolle über alle Quellen */
  effective: Role | null;
  external: boolean;
  guest: boolean;
  /** Gastlinks, über die die Person beigetreten ist */
  links: { id: string; name: string }[];
}

export interface SharingGroup {
  id: string;
  name: string;
  color: GroupColor;
  role: Role;
  memberCount: number;
}

export interface SharingCandidate {
  kind: 'member' | 'group';
  id: string;
  name: string;
  /** Teammitglied: E-Mail; Gruppe: Zahl der Mitglieder */
  detail: string;
  color?: GroupColor;
}

export interface BoardSharing {
  teamId: string | null;
  teamName: string | null;
  people: SharingPerson[];
  groups: SharingGroup[];
  /** Teammitglieder und Gruppen, die noch keine Freigabe haben (nur bei Team-Boards) */
  candidates: SharingCandidate[];
}

/** „Tatsächlichen Zugriff anzeigen“: effektive Rolle je Person mit allen Quellen */
export interface AccessEntry {
  id: string;
  name: string;
  image: string | null;
  role: Role;
  external: boolean;
  guest: boolean;
  sources: AccessSource[];
}

/** Externe Personen mit Zugriff auf Boards des Teams (für die Teamverwaltung) */
export interface TeamExternal {
  id: string;
  name: string;
  /** leer bei Gastzugängen ohne Konto */
  email: string;
  guest: boolean;
  boardCount: number;
}

/** Board ohne aktiven Besitzer nach einer Notfallsperre (nur Verwaltungsdaten) */
export interface OwnerlessBoard {
  id: string;
  title: string;
  since: string;
  /** Person, deren Sperre den Besitz beendet hat */
  formerOwner: string | null;
}

/** Freigaben einer Gruppe, soweit die betrachtende Person die Boards sehen darf */
export interface GroupBoards {
  boards: { id: string; title: string; role: Role }[];
  /** Es gibt weitere freigegebene Boards, die die Person nicht sieht (ohne Namen oder Zahl) */
  hidden: boolean;
}
